import { styled, css } from 'styled-components';

// ── Left sidebar wrapper (flex row: icon rail + popover) ──

export const Container = styled.div`
  ${() => css`
    position: relative;
    align-self: flex-start;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.5rem;
    flex-shrink: 0;
    z-index: 10;
  `}
`;

export const IconRail = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    width: 3rem;
    background: ${colors.brandLight};
    border: 1px solid ${colors.brandDarkTint90};
    border-radius: 0.429rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${spacings.s4} 0;
    flex-shrink: 0;
    gap: 1rem;
  `}
`;

export const IconButton = styled.button<{ $isActive?: boolean; $isPrimary?: boolean }>`
  ${({ theme: { colors }, $isActive, $isPrimary }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border: none;
    border-radius: 0.214rem;
    cursor: pointer;
    transition: all 0.15s ease;

    ${
      $isPrimary
        ? css`
            background: ${colors.brandPrimaryTint90};
            color: ${colors.brandPrimary};
          `
        : css`
            background: ${$isActive ? colors.brandDarkTint95 : 'transparent'};
            color: ${colors.brandDark};
          `
    }

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  `}
`;

// ── Sidebar popover panels ──

export const PopoverPanel = styled.div`
  ${({ theme: { colors } }) => css`
    background: ${colors.brandLight};
    border: 1px solid ${colors.brandDarkTint90};
    border-radius: 0.5rem;
    width: 22rem;
    max-height: calc(100vh - 10rem);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
  `}
`;

export const PopoverTitleBar = styled.div`
  ${({ theme: { colors, fontSizes, fontWeights, spacings } }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${spacings.s12} ${spacings.s16};
    border-bottom: 1px solid ${colors.brandDarkTint90};
    flex-shrink: 0;

    span {
      font-size: ${fontSizes.s16};
      font-weight: ${fontWeights.medium};
      color: ${colors.brandDark};
    }
  `}
`;

export const PopoverCloseButton = styled.button`
  ${({ theme: { colors } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    background: transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    color: ${colors.brandDarkAlpha50};

    svg {
      width: 1rem;
      height: 1rem;
    }

    &:hover {
      background: ${colors.brandDarkTint95};
      color: ${colors.brandDark};
    }
  `}
`;

export const PopoverHeader = styled.div`
  ${({ theme: { colors, fontWeights, spacings } }) => css`
    padding: ${spacings.s12} ${spacings.s12};
    font-size: 0.75rem;
    font-weight: ${fontWeights.semiBold};
    color: ${colors.brandDarkAlpha50};
    text-transform: none;
    letter-spacing: 0;
    border-bottom: 1px solid ${colors.brandDarkTint90};
  `}
`;

export const SearchWrapper = styled.div`
  ${({ theme: { spacings } }) => css`
    padding: ${spacings.s8} ${spacings.s12};

    input {
      margin: 0;
    }
  `}
`;

// ── Filter tabs (Network / Visual type / Elements) ──

export const FilterTabsRow = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    justify-content: space-between;
    gap: ${spacings.s4};
    padding: ${spacings.s8} ${spacings.s12};
    flex-shrink: 0;
  `}
`;

export const FilterTabButton = styled.button<{ $isActive?: boolean }>`
  ${({ theme: { colors, fontSizes, fontWeights }, $isActive }) => css`
    padding: 0.429rem 1.786rem;
    border: none;
    border-radius: 9999px;
    background: ${$isActive ? colors.brandDarkTint90 : 'transparent'};
    color: ${$isActive ? colors.brandDark : colors.brandDarkAlpha50};
    font-size: ${fontSizes.s12};
    font-weight: ${$isActive ? fontWeights.medium : fontWeights.regular};
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
      background: ${$isActive ? colors.brandDarkTint90 : colors.brandDarkTint95};
      color: ${colors.brandDark};
    }
  `}
`;

// ── Network filter icon row ──

export const NetworkFilterRow = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    padding: ${spacings.s8} ${spacings.s12};
    flex-wrap: wrap;
    flex-shrink: 0;
    border-bottom: 1px solid ${colors.brandDarkTint90};
  `}
`;

export const NetworkFilterButton = styled.button<{ $isActive?: boolean }>`
  ${({ theme: { colors }, $isActive }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border: transparent;
    border-radius: 0.375rem;
    background: ${$isActive ? colors.brandPrimaryTint95 : 'transparent'};
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    &:hover {
      background: ${$isActive ? colors.brandPrimaryTint95 : colors.brandDarkTint95};
    }
  `}
`;

// ── Chart type icon filter row ──

export const ChartTypeRow = styled.div`
  ${({ theme: { spacings, colors } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s4};
    padding: ${spacings.s8} ${spacings.s12} ${spacings.s4};
    flex-wrap: wrap;
    flex-shrink: 0;
    border-bottom: 1px solid ${colors.brandDarkTint90};
  `}
`;

export const ChartTypeIconButton = styled.button<{ $isActive?: boolean }>`
  ${({ theme: { colors }, $isActive }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border: transparent;
    border-radius: 0.25rem;
    background: ${$isActive ? colors.brandPrimaryTint95 : 'transparent'};
    color: ${$isActive ? colors.brandPrimary : 'inherit'};
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: ${$isActive ? colors.brandPrimaryTint95 : colors.brandDarkTint95};
      color: ${$isActive ? colors.brandPrimary : colors.brandDark};
    }
  `}
`;

export const ChartTypeSectionLabel = styled.p`
  ${({ theme: { colors } }) => css`
    padding: 0.5rem 0.75rem 0.25rem;
    font-size: 0.8125rem;
    color: ${colors.brandDarkAlpha50};
    margin: 0;
    flex-shrink: 0;
  `}
`;

export const SectionHeader = styled.button<{ $isExpanded?: boolean }>`
  ${({ theme: { colors, fontWeights, spacings }, $isExpanded }) => css`
    display: flex;
    align-items: center;
    gap: ${spacings.s8};
    width: 100%;
    padding: ${spacings.s8} ${spacings.s8};
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: ${fontWeights.medium};
    color: ${colors.brandDarkAlpha50};
    text-align: left;
    letter-spacing: 0;
    transition: all 0.15s ease;

    svg {
      width: 0.5rem;
      height: 0.5rem;
      transition: transform 0.15s ease;
      transform: rotate(${$isExpanded ? '0deg' : '-90deg'});
      flex-shrink: 0;
    }

    &:hover {
      color: ${colors.brandDark};
    }
  `}
`;

// ── Module drop list ──

export const ModuleDropList = styled.ul`
  ${({ theme: { colors } }) => css`
    margin: 0;
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0.5rem;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.brandLight};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${colors.brandDarkTint80};
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: ${colors.brandDarkTint70};
    }
  `}
`;

export const ModuleDropItem = styled.li`
  ${({ theme: { colors } }) => css`
    align-items: center;
    border-radius: 0.375rem;
    border: 1px solid ${colors.brandDarkTint90};
    background: ${colors.brandLight};
    color: ${colors.brandDark};
    cursor: pointer;
    display: flex;
    gap: 0.5rem;
    padding: 1.143rem 1rem 1.143rem 0.5rem;
    position: relative;
    user-select: none;

    &:hover {
      background: ${colors.brandPrimaryAlpha5};
      border-color: ${colors.brandDarkTint80};
    }
  `}
`;

export const ModuleDragHandle = styled.div`
  width: 8px;
  height: 14px;
  background-image: radial-gradient(circle, currentColor 1.5px, transparent 1.5px);
  background-size: 4px 5px;
  opacity: 0.3;
  flex-shrink: 0;
`;

export const ModuleLabelGroup = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

export const ModuleAvatar = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: ${colors.brandPrimaryTint95};
    border-radius: 0.5rem;
    color: ${colors.brandPrimary};
    display: flex;
    flex: 0 0 auto;
    height: 2rem;
    justify-content: center;
    position: relative;
    width: 2rem;

    svg {
      width: 1rem;
      height: 1rem;
    }
  `}
`;

export const ModuleItemLabel = styled.p`
  ${({ theme: { fontSizes } }) => css`
    display: flex;
    font-size: ${fontSizes.s14};
    flex-direction: column;
    line-height: 1.25em;
    flex: 1;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const ModulePlatformBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const SkeletonWrapper = styled.div`
  padding: 0;
`;

export const ModuleDropItemDisabled = styled(ModuleDropItem)`
  cursor: default;
  color: inherit;
  opacity: 0.5;

  &:hover {
    background: transparent;
  }
`;
